/**
 * User API Service
 */

import { api } from "@/lib/api";
import type {
  User,
  UserListResponse,
  UserCreateData,
  UserUpdateData,
  ChangePasswordData,
  UserFilters,
  Role,
  Sucursal,
} from "@/types/user";

const USERS_URL = "/users/";
const ROLES_URL = "/roles/";
const SUCURSALES_URL = "/sucursales/";

/**
 * Build query string from filters
 */
function buildQueryString(filters: UserFilters): string {
  const params = new URLSearchParams();

  if (filters.search) params.append("search", filters.search);
  if (filters.role) params.append("role", String(filters.role));
  if (filters.sucursal) params.append("sucursal", String(filters.sucursal));
  if (filters.is_active !== undefined) params.append("is_active", String(filters.is_active));
  if (filters.page) params.append("page", String(filters.page));
  if (filters.page_size) params.append("page_size", String(filters.page_size));
  if (filters.ordering) params.append("ordering", filters.ordering);

  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
}

/**
 * Get paginated list of users with optional filters
 */
export async function getUsers(filters: UserFilters = {}): Promise<UserListResponse> {
  const queryString = buildQueryString(filters);
  const response = await api.get<UserListResponse>(`${USERS_URL}${queryString}`);
  return response.data;
}

/**
 * Get a single user by ID
 */
export async function getUser(id: number): Promise<User> {
  const response = await api.get<User>(`${USERS_URL}${id}/`);
  return response.data;
}

/**
 * Create a new user
 */
export async function createUser(data: UserCreateData): Promise<User> {
  const response = await api.post<User>(USERS_URL, data);
  return response.data;
}

/**
 * Update an existing user
 */
export async function updateUser(id: number, data: UserUpdateData): Promise<User> {
  const response = await api.patch<User>(`${USERS_URL}${id}/`, data);
  return response.data;
}

/**
 * Delete (deactivate) a user
 */
export async function deleteUser(id: number): Promise<void> {
  await api.delete(`${USERS_URL}${id}/`);
}

/**
 * Activate a user
 */
export async function activateUser(id: number): Promise<User> {
  const response = await api.post<User>(`${USERS_URL}${id}/activate/`);
  return response.data;
}

/**
 * Deactivate a user
 */
export async function deactivateUser(id: number): Promise<User> {
  const response = await api.post<User>(`${USERS_URL}${id}/deactivate/`);
  return response.data;
}

/**
 * Change user password
 */
export async function changePassword(id: number, data: ChangePasswordData): Promise<void> {
  await api.post(`${USERS_URL}${id}/change-password/`, data);
}

/**
 * Get all roles
 */
export async function getRoles(): Promise<Role[]> {
  const response = await api.get<Role[] | { results: Role[] }>(ROLES_URL);
  // Handle both array and paginated response
  if (Array.isArray(response.data)) {
    return response.data;
  }
  return response.data.results || [];
}

/**
 * Get all sucursales
 */
export async function getSucursales(): Promise<Sucursal[]> {
  const response = await api.get<Sucursal[] | { results: Sucursal[] }>(SUCURSALES_URL);
  // Handle both array and paginated response
  if (Array.isArray(response.data)) {
    return response.data;
  }
  return response.data.results || [];
}
